/**
 * WorkflowScheduler - Advanced scheduling interface
 * ZAPHUB 2.0 - Cron editor, calendar preview, timezone selector
 */

import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Switch } from './ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Clock,
  Calendar,
  Globe,
  Repeat,
  Play,
  Pause,
  Trash2,
  Plus,
  AlertCircle,
  CheckCircle,
  Zap,
  Sun,
  Moon,
  Edit,
} from 'lucide-react';
import type { WorkflowSchedule, HolidayCalendar } from '@shared/types';

interface WorkflowSchedulerProps {
  workflowId?: string;
  workflowName?: string;
  onScheduleCreated?: (schedule: WorkflowSchedule) => void;
}

export const WorkflowScheduler: React.FC<WorkflowSchedulerProps> = ({
  workflowId = 'demo-workflow',
  workflowName = 'Workflow Demo',
  onScheduleCreated,
}) => {
  const [schedules, setSchedules] = useState<WorkflowSchedule[]>([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [scheduleType, setScheduleType] = useState<'cron' | 'interval' | 'once'>('interval');
  const [cronExpression, setCronExpression] = useState('0 0 * * *');
  const [intervalValue, setIntervalValue] = useState(1);
  const [intervalUnit, setIntervalUnit] = useState<'minutes' | 'hours' | 'days' | 'weeks'>('hours');
  const [runAt, setRunAt] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  const [skipWeekends, setSkipWeekends] = useState(false);
  const [skipHolidays, setSkipHolidays] = useState(false);
  const [holidayCalendar, setHolidayCalendar] = useState('us-federal');
  const [upcomingRuns, setUpcomingRuns] = useState<Date[]>([]);

  const timezones = [
    'UTC',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Australia/Sydney',
  ];

  const commonCronExpressions = [
    { label: 'Every minute', value: '* * * * *' },
    { label: 'Every 5 minutes', value: '*/5 * * * *' },
    { label: 'Every 15 minutes', value: '*/15 * * * *' },
    { label: 'Every 30 minutes', value: '*/30 * * * *' },
    { label: 'Every hour', value: '0 * * * *' },
    { label: 'Every 6 hours', value: '0 */6 * * *' },
    { label: 'Every day at midnight', value: '0 0 * * *' },
    { label: 'Every day at 9 AM', value: '0 9 * * *' },
    { label: 'Weekdays at 9 AM', value: '0 9 * * 1-5' },
    { label: 'Every Monday at 9 AM', value: '0 9 * * 1' },
    { label: 'First day of month', value: '0 0 1 * *' },
  ];

  useEffect(() => {
    fetchSchedules();
  }, [workflowId]);

  useEffect(() => {
    if (scheduleType === 'cron' && cronExpression) {
      previewSchedule();
    }
  }, [cronExpression, timezone, skipWeekends, skipHolidays]);

  const fetchSchedules = async () => {
    // Mock - in production, call API
    const mockSchedules: WorkflowSchedule[] = [
      {
        id: 'sched-1',
        workflowId,
        type: 'cron',
        enabled: true,
        cronExpression: '0 9 * * 1-5',
        timezone: 'America/New_York',
        skipWeekends: true,
        nextRun: new Date(Date.now() + 24 * 60 * 60 * 1000),
        lastRun: new Date(Date.now() - 24 * 60 * 60 * 1000),
        createdAt: new Date('2024-01-15'),
        updatedAt: new Date('2024-01-15'),
      },
    ];

    setSchedules(mockSchedules);
  };

  const previewSchedule = async () => {
    // Mock - in production, call API to get upcoming runs
    const runs: Date[] = [];
    const now = new Date();

    for (let i = 0; i < 5; i++) {
      const next = new Date(now);
      next.setDate(next.getDate() + i + 1);
      next.setHours(9, 0, 0, 0);

      // Skip weekends if enabled
      if (skipWeekends && (next.getDay() === 0 || next.getDay() === 6)) {
        continue;
      }

      runs.push(next);
    }

    setUpcomingRuns(runs);
  };

  const handleCreateSchedule = async () => {
    const newSchedule: Partial<WorkflowSchedule> = {
      workflowId,
      type: scheduleType,
      enabled: true,
      timezone,
      skipWeekends,
      skipHolidays: skipHolidays ? true : undefined,
      holidayCalendar: skipHolidays ? holidayCalendar : undefined,
    };

    if (scheduleType === 'cron') {
      newSchedule.cronExpression = cronExpression;
    } else if (scheduleType === 'interval') {
      newSchedule.interval = {
        value: intervalValue,
        unit: intervalUnit,
      };
    } else if (scheduleType === 'once') {
      newSchedule.runAt = new Date(runAt);
    }

    // In production, call API
    console.log('Creating schedule:', newSchedule);

    setShowCreateDialog(false);
    fetchSchedules();

    if (onScheduleCreated) {
      onScheduleCreated(newSchedule as WorkflowSchedule);
    }
  };

  const handleToggleSchedule = async (scheduleId: string, enabled: boolean) => {
    // In production, call API
    console.log('Toggling schedule:', scheduleId, enabled);
    fetchSchedules();
  };

  const handleDeleteSchedule = async (scheduleId: string) => {
    if (!confirm('¿Eliminar este horario?')) return;

    // In production, call API
    console.log('Deleting schedule:', scheduleId);
    fetchSchedules();
  };

  const cronToHuman = (cron: string): string => {
    const found = commonCronExpressions.find((e) => e.value === cron);
    if (found) return found.label;

    // Simplified conversion
    const parts = cron.split(' ');
    if (cron === '0 0 * * *') return 'Every day at midnight';
    if (cron === '0 9 * * 1-5') return 'Weekdays at 9 AM';
    return 'Custom schedule';
  };

  const formatNextRun = (date: Date): string => {
    const now = new Date();
    const diff = date.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (days > 0) return `in ${days} day${days > 1 ? 's' : ''}`;
    if (hours > 0) return `in ${hours} hour${hours > 1 ? 's' : ''}`;
    return 'soon';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl mb-1">Workflow Scheduler</h2>
          <p className="text-gray-600">Configure cuando se ejecuta "{workflowName}"</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Horario
        </Button>
      </div>

      {/* Active Schedules */}
      {schedules.length > 0 ? (
        <div className="grid gap-4">
          {schedules.map((schedule) => (
            <Card key={schedule.id} className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <Badge variant={schedule.enabled ? 'default' : 'secondary'}>
                      {schedule.enabled ? 'Active' : 'Paused'}
                    </Badge>
                    <Badge variant="outline">
                      {schedule.type === 'cron' ? (
                        <>
                          <Repeat className="h-3 w-3 mr-1" />
                          Cron
                        </>
                      ) : schedule.type === 'interval' ? (
                        <>
                          <Clock className="h-3 w-3 mr-1" />
                          Interval
                        </>
                      ) : (
                        <>
                          <Calendar className="h-3 w-3 mr-1" />
                          One-time
                        </>
                      )}
                    </Badge>
                    <Badge variant="outline">
                      <Globe className="h-3 w-3 mr-1" />
                      {schedule.timezone}
                    </Badge>
                  </div>

                  <div className="space-y-2 mb-4">
                    {schedule.type === 'cron' && (
                      <div>
                        <span className="text-sm text-gray-600">Schedule: </span>
                        <span className="font-mono bg-gray-100 px-2 py-1 rounded text-sm">
                          {schedule.cronExpression}
                        </span>
                        <span className="text-sm text-gray-600 ml-2">
                          ({cronToHuman(schedule.cronExpression || '')})
                        </span>
                      </div>
                    )}

                    {schedule.type === 'interval' && schedule.interval && (
                      <div>
                        <span className="text-sm text-gray-600">Runs every: </span>
                        <span className="font-medium">
                          {schedule.interval.value} {schedule.interval.unit}
                        </span>
                      </div>
                    )}

                    <div className="flex items-center gap-4 text-sm">
                      {schedule.nextRun && (
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-green-500" />
                          <span className="text-gray-600">Next run:</span>
                          <span className="font-medium">
                            {schedule.nextRun.toLocaleString()}
                          </span>
                          <Badge variant="secondary">{formatNextRun(schedule.nextRun)}</Badge>
                        </div>
                      )}

                      {schedule.lastRun && (
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-blue-500" />
                          <span className="text-gray-600">Last run:</span>
                          <span className="font-medium">
                            {schedule.lastRun.toLocaleString()}
                          </span>
                        </div>
                      )}
                    </div>

                    {(schedule.skipWeekends || schedule.skipHolidays) && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <AlertCircle className="h-4 w-4" />
                        <span>
                          Skips:{' '}
                          {[
                            schedule.skipWeekends ? 'Weekends' : null,
                            schedule.skipHolidays ? `Holidays (${schedule.holidayCalendar})` : null,
                          ]
                            .filter(Boolean)
                            .join(', ')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleSchedule(schedule.id, !schedule.enabled)}
                  >
                    {schedule.enabled ? (
                      <>
                        <Pause className="h-4 w-4 mr-1" />
                        Pause
                      </>
                    ) : (
                      <>
                        <Play className="h-4 w-4 mr-1" />
                        Resume
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteSchedule(schedule.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="p-12 text-center">
          <Clock className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl mb-2">No schedules configured</h3>
          <p className="text-gray-600 mb-4">
            Create your first schedule to automate this workflow
          </p>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Schedule
          </Button>
        </Card>
      )}

      {/* Create Schedule Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Create New Schedule</DialogTitle>
            <DialogDescription>
              Configure when and how often this workflow should run
            </DialogDescription>
          </DialogHeader>

          <Tabs value={scheduleType} onValueChange={(v: any) => setScheduleType(v)}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="interval">
                <Repeat className="h-4 w-4 mr-2" />
                Interval
              </TabsTrigger>
              <TabsTrigger value="cron">
                <Clock className="h-4 w-4 mr-2" />
                Cron
              </TabsTrigger>
              <TabsTrigger value="once">
                <Calendar className="h-4 w-4 mr-2" />
                One-time
              </TabsTrigger>
            </TabsList>

            <TabsContent value="interval" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Run every</Label>
                  <Input
                    type="number"
                    min={1}
                    value={intervalValue}
                    onChange={(e) => setIntervalValue(parseInt(e.target.value))}
                  />
                </div>
                <div>
                  <Label>Unit</Label>
                  <Select value={intervalUnit} onValueChange={(v: any) => setIntervalUnit(v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="minutes">Minutes</SelectItem>
                      <SelectItem value="hours">Hours</SelectItem>
                      <SelectItem value="days">Days</SelectItem>
                      <SelectItem value="weeks">Weeks</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="flex items-start gap-2">
                  <Zap className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-900">Will run every:</p>
                    <p className="text-blue-700">
                      {intervalValue} {intervalUnit}
                    </p>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="cron" className="space-y-4">
              <div>
                <Label>Cron Expression</Label>
                <Input
                  value={cronExpression}
                  onChange={(e) => setCronExpression(e.target.value)}
                  placeholder="0 0 * * *"
                  className="font-mono"
                />
                <p className="text-sm text-gray-600 mt-1">
                  Current: {cronToHuman(cronExpression)}
                </p>
              </div>

              <div>
                <Label className="mb-2 block">Common Expressions</Label>
                <div className="grid grid-cols-2 gap-2">
                  {commonCronExpressions.slice(0, 6).map((expr) => (
                    <Button
                      key={expr.value}
                      variant="outline"
                      size="sm"
                      onClick={() => setCronExpression(expr.value)}
                      className="justify-start"
                    >
                      {expr.label}
                    </Button>
                  ))}
                </div>
              </div>

              {upcomingRuns.length > 0 && (
                <div className="p-4 bg-green-50 rounded-lg">
                  <div className="flex items-start gap-2 mb-2">
                    <Calendar className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <p className="font-medium text-green-900">Upcoming runs:</p>
                  </div>
                  <div className="space-y-1 ml-7">
                    {upcomingRuns.slice(0, 5).map((run, idx) => (
                      <p key={idx} className="text-sm text-green-700">
                        {run.toLocaleString()}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="once" className="space-y-4">
              <div>
                <Label>Run at</Label>
                <Input
                  type="datetime-local"
                  value={runAt}
                  onChange={(e) => setRunAt(e.target.value)}
                />
              </div>

              {runAt && (
                <div className="p-4 bg-purple-50 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Calendar className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-purple-900">Will run once at:</p>
                      <p className="text-purple-700">{new Date(runAt).toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>

          {/* Common Settings */}
          <div className="space-y-4 pt-4 border-t">
            <div>
              <Label>Timezone</Label>
              <Select value={timezone} onValueChange={setTimezone}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {timezones.map((tz) => (
                    <SelectItem key={tz} value={tz}>
                      {tz}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Skip Weekends</Label>
                <p className="text-sm text-gray-600">Don't run on Saturday or Sunday</p>
              </div>
              <Switch checked={skipWeekends} onCheckedChange={setSkipWeekends} />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Skip Holidays</Label>
                <p className="text-sm text-gray-600">Skip public holidays</p>
              </div>
              <Switch checked={skipHolidays} onCheckedChange={setSkipHolidays} />
            </div>

            {skipHolidays && (
              <div>
                <Label>Holiday Calendar</Label>
                <Select value={holidayCalendar} onValueChange={setHolidayCalendar}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="us-federal">US Federal Holidays</SelectItem>
                    <SelectItem value="uk-bank">UK Bank Holidays</SelectItem>
                    <SelectItem value="eu-common">EU Common Holidays</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateSchedule}>
              <CheckCircle className="h-4 w-4 mr-2" />
              Create Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
